// no-explicit-any

import React, { useState, useRef, useEffect } from 'react'
import { Input, Stack } from 'rsuite'
import { ReactComponent as WarningIcon } from '../../../assets/svgs/warning.svg'
import { ReactComponent as Loading } from '../../../assets/svgs/loading.svg'
import { Button } from '../../atoms/button/button'
import { useNavigate } from 'react-router-dom'

import { t } from '../../../utils/i18n'
import './verify-otp.scss'
import { formatTime } from '../../../utils/helper-functions'
import { showToast } from '../../atoms/toaster/toaster'
import { useStorage } from '../../../customHooks/useStorage'
import axios from '../../../utils/axios'
import { useAuth } from '../../../customHooks/useAuth'
import { SessionToken } from '../signin-form/signin-form'
import { User } from '../../../api/signin'

interface VerifyOtpSection {
  error?: string
  handleChangeEmail?: (isTrue: boolean) => void
  handleEmailLogin?: () => void
  linkAccountView?: boolean
  setShowOTPScreen?: (isTrue: boolean) => void
  setPageRefresh?: (isTrue: boolean) => void
  setEmail?: (val: string) => void
}

const VerifyOtpSection = ({
  error,
  handleChangeEmail,
  handleEmailLogin,
  linkAccountView,
  setShowOTPScreen,
  setPageRefresh,
  setEmail,
}: VerifyOtpSection) => {
  const OTP_LENGTH = process.env.REACT_APP_OTP_LENGTH as string

  const MAX_ATTEMPTS = process.env.REACT_APP_MAX_OTP_ATTEMPTS as string

  const BLOCK_DURATION_MINUTES = process.env.REACT_APP_BLOCK_DURATION_MINUTES as string

  const BLOCK_RESEND_SECONDS = process.env.REACT_APP_RESEND_OTP_DURATION_SECONDS as string

  const {
    value: otpBlockTime,
    setValue: setOtpBlock,
    remove: removeOTPBlock,
  } = useStorage<string | null>('otp_block_until', null, 'local')

  const {
    value: LinkAccBlockTime,
    setValue: setLinkAccBlock,
    remove: removeLinkAccBlock,
  } = useStorage<string | null>('link_acc_block_until', null, 'local')

  const {
    value: resendBlock,
    setValue: setResendBlock,
    remove: removeResendBlock,
  } = useStorage<string | null>('otp_resend_block_until', null, 'local')

  const navigate = useNavigate()
  const { user } = useAuth()

  const { value: sessionToken, remove: removeSessionToken } = useStorage<SessionToken>(
    'token',
    { token: '', email: '' },
    'session'
  )

  const { setValue: setUserSession } = useStorage<User | null>('user', null, 'session')

  const { setUser, setIsAuthenticated } = useAuth()
  const [otp, setOtp] = useState<string[]>(Array(Number(OTP_LENGTH)).fill(''))
  const [attempts, setAttempts] = useState<number>(0)
  const [blockTimeLeft, setBlockTimeLeft] = useState(0) // in seconds
  const [isBlocked, setIsBlocked] = useState<boolean>(
    linkAccountView ? Boolean(LinkAccBlockTime) : Boolean(otpBlockTime)
  )
  const [isOtpError, setIsOtpError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [resendBlockTimerLeft, setResendBlockTimerLeft] = useState<number>(0) // in seconds
  const [isResendBlocked, setIsResendBlocked] = useState<boolean>(Boolean(resendBlock))
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const resendTimerRef = useRef<NodeJS.Timeout | null>(null)

  const isOtpComplete = otp.every((digit) => digit !== '')

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return

    const updatedOtp = [...otp]
    updatedOtp[index] = value
    setOtp(updatedOtp)

    if (value && index < Number(OTP_LENGTH) - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleResend = () => {
    setIsResendBlocked(true)
    const resendBlockDuration = Number(BLOCK_RESEND_SECONDS) // 30 seconds

    setResendBlockTimerLeft(resendBlockDuration)

    const resendUnblockTime = Date.now() + resendBlockDuration * 1000 // in ms
    setResendBlock(resendUnblockTime.toString())
    handleEmailLogin?.()
    showToast({ message: t('opt.resend.msg') })
  }

  const handleOTPSignin = async () => {
    try {
      if (linkAccountView) {
        const payload = {
          accountToLink: sessionToken.email,
          otp: otp.join(''),
        }
        const config = {
          url: process.env.REACT_APP_LINK_ACCOUNT_WITH_OTP,
          method: 'POST',
          data: payload,
          headers: {
            'x-user': user?.email,
            'x-access-token': user?.tokens?.accessToken?.token,
            'x-source': user?.isSSO ? 'sso' : null,
          },
        }
        const res = await axios(config)
        if (res.data.data.status === 'ok') {
          removeSessionToken()
          showToast({
            message: t('account.link.success.title'),
            description: t('account.link.success.desc'),
          })
          setPageRefresh?.(true)
          setIsLoading(true)
          setEmail?.('')
          setInterval(() => {
            setShowOTPScreen?.(false)
          }, 1000)
        }
      } else {
        const payload = {
          session: sessionToken.token,
          email: sessionToken.email,
          code: otp.join(''),
        }

        const config = {
          url: process.env.REACT_APP_VERIFY_OTP,
          method: 'POST',
          data: payload,
        }
        const res = await axios(config)
        if (res.data) {
          setIsLoading(true)
          const result: User = {
            name: res.data.data.name,
            email: sessionToken.email,
            tokens: res.data.data.tokens,
            user_type: res.data.data.user_type,
            businessUser: res.data.data.businessUser,
            collabUser: res.data.data.collabUser,
            isSSO: false,
            authenticated: true,
            isVerifyOtp: true,
            authorisedApp: res?.data?.data?.authorisedApp,
            roles: res?.data?.data?.roles,
          }
          setUser(result)
          removeSessionToken()
          setUserSession(result)
          verifyOTP(true)
        } else {
          verifyOTP(false)
        }
      }
    } catch {
      verifyOTP(false)
    }
  }
  // Called on clicking verify
  const verifyOTP = (isValid: boolean) => {
    if (isBlocked) return
    if (!isValid) {
      setIsOtpError(true)
      const failedAttempts = attempts + 1
      setAttempts(failedAttempts)
      if (attempts >= Number(MAX_ATTEMPTS) - 1) {
        blockUser()
      }
      setOtp(Array(Number(OTP_LENGTH) | 8).fill(''))
    } else {
      setAttempts(0) // Reset on success
      setOtp(Array(Number(OTP_LENGTH) | 8).fill(''))
      setIsLoading(true)
      setIsAuthenticated(true)
      navigate('/overview')
    }
  }
  // Block user for 20 minutes
  const blockUser = () => {
    setIsBlocked(true)
    const blockDuration = Number(BLOCK_DURATION_MINUTES) * 60 // 20 mins in seconds
    setBlockTimeLeft(blockDuration)

    const unblockTime = Date.now() + blockDuration * 1000 // in ms

    if (linkAccountView) {
      setLinkAccBlock(unblockTime.toString())
    } else {
      setOtpBlock(unblockTime.toString())
    }

    timerRef.current = setInterval(() => {
      setBlockTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!)
          setIsBlocked(false)
          setAttempts(0)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleEmailChange = () => {
    handleChangeEmail?.(false)
  }

  const otpExceededMessage = t('opt.attempts.exceeded').replace(
    '{{timer}}',
    formatTime(blockTimeLeft)
  )

  useEffect(() => {
    inputRefs.current[0]?.focus()
    setIsResendBlocked(Boolean(resendBlock))
  }, [])

  useEffect(() => {
    const unblockTimeStr = LinkAccBlockTime ? LinkAccBlockTime : otpBlockTime
    if (unblockTimeStr) {
      const unblockTime = parseInt(unblockTimeStr, 10)
      const timeLeft = Math.floor((unblockTime - Date.now()) / 1000)

      if (timeLeft > 0) {
        setBlockTimeLeft(timeLeft)

        timerRef.current = setInterval(() => {
          setBlockTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current!)
              setIsBlocked(false)
              setAttempts(0)
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              linkAccountView ? removeLinkAccBlock() : removeOTPBlock()
              return 0
            }
            return prev - 1
          })
        }, 1000)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        linkAccountView ? removeLinkAccBlock() : removeOTPBlock()
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [otpBlockTime, LinkAccBlockTime, removeOTPBlock, removeOTPBlock])

  useEffect(() => {
    const unblockResendTimeStr = resendBlock
    if (unblockResendTimeStr) {
      const unblockTime = parseInt(unblockResendTimeStr, 10)
      const timeLeft = Math.floor((unblockTime - Date.now()) / 1000)

      if (timeLeft > 0) {
        setResendBlockTimerLeft(timeLeft)

        resendTimerRef.current = setInterval(() => {
          setResendBlockTimerLeft((prev) => {
            if (prev <= 1) {
              // eslint-disable-next-line
              clearInterval(resendTimerRef?.current!)
              setIsResendBlocked(false)
              removeResendBlock()
              return 0
            }
            return prev - 1
          })
        }, 1000)
      } else {
        removeResendBlock()
      }
    }
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current)
    }
  }, [isResendBlocked, resendBlockTimerLeft, removeResendBlock, resendBlock])

  return (
    <div className="otp-form">
      <h3 className="otp-title">{linkAccountView ? sessionToken.email : t('verify.otp')}</h3>
      {!linkAccountView && (
        <>
          {' '}
          <p className="otp-detail">{t('opt.sent').replace('{{email}}', sessionToken.email)}</p>
          <Button
            label={t('change.cta')}
            buttonWithText
            className="change-email-btn"
            handleClick={handleEmailChange}
          />
        </>
      )}

      <Stack spacing={15} className="otp-inputs">
        {otp.map((digit, i) => (
          <Input
            key={i}
            type="text"
            maxLength={1}
            value={digit}
            disabled={isBlocked}
            inputRef={(ref) => (inputRefs.current[i] = ref)}
            onChange={(value) => handleChange(value, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            className={`otp-box ${isOtpError ? 'error' : ''}`}
          />
        ))}
      </Stack>
      {(isBlocked || isOtpError) && (
        <div className="error-message">
          <WarningIcon className="warning-symbol" />
          {isBlocked ? otpExceededMessage : error}
        </div>
      )}
      <div className={`${linkAccountView ? 'btn-group' : ''}`}>
        {linkAccountView && <p className="">{t('resend.question')}</p>}
        <Button
          label={t('resend.cta')}
          buttonWithText
          className="resend-otp-btn"
          handleClick={handleResend}
          disabled={isBlocked || isResendBlocked}
        />
      </div>
      {isResendBlocked && (
        <p className="otp-resent body-sm">
          {t('opt.resend.duration').replace('{{timer}}', resendBlockTimerLeft)}
        </p>
      )}

      <Button
        label={linkAccountView ? t('link.now') : t('sign.in')}
        disabled={!isOtpComplete}
        handleClick={handleOTPSignin}
      />

      {isLoading && (
        <div className="loading-overlay">
          <Loading className="spinner" />
        </div>
      )}
    </div>
  )
}

export default VerifyOtpSection
